import { Component, nextTick, watch } from 'vue';
import { AxiosResponse } from 'axios';
import { cloneDeep, isEmpty, omit } from 'lodash-es';
import { Bus, useAside } from '@orion.ui/orion';
import { qsFrom64, qsTo64 } from '@/utils/tools';
import router from '@/router/index.router';
import useSelfService from './SelfService';
import useNavLoaderService from './NavLoaderService';
import type BasePagedListSetupService from '@/setup/BasePagedListSetupService';

export default abstract class BasePagedListFilterService<F extends IPagedListParameters, T extends { id: number }> {
	abstract filter: F;
  abstract get quickFilters (): ListQuickFilter<F>[] | undefined;
	protected abstract filterComponent?: Component;
	protected abstract get api (): {
		grid: (...args: any[]) => Promise<AxiosResponse<IPagedList<T>>>,
	}

	private setupConnections = [] as { list: BasePagedListSetupService<F, T>, _popable?: OrionAside | OrionModal}[];
	protected useSearchKey = false;
	protected filterProps?: Record<string, any>;
	protected originalFilter = {} as Omit<F, 'page' | 'research'>;
	protected lastFilter = {};
	protected commonFilter = {
		order: {
			property: 'Id',
			ascending: false,
		},
		page: {
			index: 1,
			size: 20,
		},
		research: undefined as Undef<string>,
		tagIds: undefined as Undef<number[]>,
	};

	private get connectedList () { return this.setupConnections.last()?.list; }
	private get connectedListPopable () { return this.setupConnections.last()?._popable; }

	get self () { return useSelfService().self; }

	get queryFilter () { return qsFrom64(router.currentRoute.value.query.filter?.toString()); }

	get filterIsDifferentFromOriginal () {
		const current = omit(this.filter, 'page', 'research');
		const original = omit(this.originalFilter, 'page', 'research');
		return JSON.stringify(current) !== JSON.stringify(original);
	}

	get originalFilterSignature () {
		return Object.freeze(this.originalFilter);
	}

	get customFilter (): Record<string, any> {
		return {};
	}


	constructor () {
		nextTick(() => this.storeOriginalFilter());

		watch(() => router.currentRoute.value, (to, from) => {
			if (to.name === this.connectedList?.routeName.listRouteName) {
				if (from.name !== this.connectedList?.routeName.detailsRouteName) {
					const previousPageIndex = qsFrom64(from.query.filter?.toString())?.page?.index;
					const nextPageIndex = this.queryFilter?.page?.index;
					this.applyQueryFilter(isEmpty(to.query.filter) || (previousPageIndex === nextPageIndex));
				}
			}
		});

		watch(() => router.currentRoute.value.query, (val, old) => {
			if (!this.connectedList?.useLayoutAsideForDetails) return;
			if (router.currentRoute.value.name === this.connectedList?.routeName.detailsRouteName) {
				if (val.filter !== old.filter) {
					const previousPageIndex = qsFrom64(old.filter?.toString())?.page?.index;
					const nextPageIndex = this.queryFilter?.page?.index;
					this.applyQueryFilter(previousPageIndex === nextPageIndex);
				}
			}
		});
	}


	protected async hookBeforeApplyFilterAsync () {}

	private applyQueryFilter (resetPagination?: boolean) {
		this.reset();
		Object.assign(this.filter, this.queryFilter);
		this.applyAsync(resetPagination);
	}

	private getOriginalFilterKeyValue (key: keyof Omit<F, 'page' | 'research'>) {
		return this.originalFilter[key];
	}

	setOriginalFilterKeyValue (key: keyof Omit<F, 'page' | 'research'>) {
		this.filter[key] = this.getOriginalFilterKeyValue(key);
	}

	storeOriginalFilter () {
		Object.assign(this.originalFilter, cloneDeep(omit(this.filter, 'page', 'research')));
	}

	storeLastFilter () {
		Object.assign(this.lastFilter, cloneDeep(omit(this.filter, 'page', 'research')));
	}

	connect (list: BasePagedListSetupService<any, any>, _popable?: OrionAside | OrionModal) {
		this.setupConnections.push({
			list,
			_popable,
		});
		Object.assign(this.filter, qsFrom64(router.currentRoute.value.query.filter?.toString()));
		this.applyAsync(false, _popable);
		return this;
	}

	disconnect (list: BasePagedListSetupService<any, any>) {
		this.setupConnections.deleteWhere(x => x.list.uid === list.uid);
		this.reset();
	}

	reset () {
		// Assign original values
		Object.assign(this.filter, cloneDeep(this.originalFilter));

		// Remove values that are not in original filter
		Object.keys(this.filter).delete('page').forEach((k) => {
			if (!(k in this.originalFilter)) {
				delete this.filter[k as keyof F];
			}
		});
	}

	async resetAndApplyAsync () {
		this.reset();
		await this.applyAsync(true);
	}

	cancel () {
		// Assign original values
		Object.assign(this.filter, cloneDeep(this.lastFilter));

		// Remove values that are not in original filter
		Object.keys(this.filter).delete('page').forEach((k) => {
			if (!(k in this.lastFilter)) {
				delete this.filter[(k as keyof typeof this.filter)];
			}
		});

		if (!this.filter.order) {
			this.filter.order = cloneDeep(this.commonFilter.order);
		}
	}

	async applyAsync (resetPagination?: boolean, popable?: OrionModal | OrionAside) {
		if (!this.connectedList) throw `No list has been connected to the filter service`;

		if (resetPagination) this.filter.page.index = 1;

		const targetPopable = popable ?? this.connectedListPopable;

		!targetPopable
			? useNavLoaderService().show(`Chargement des données...`)
			: targetPopable._loader()?.show(`Chargement des données...`);

		await this.connectedList.hookBeforeApplyFilterAsync();
		await this.hookBeforeApplyFilterAsync();

		const { data } = await this.api.grid({
			...this.filter,
			...this.customFilter,
			search: this.useSearchKey ? (this.filter as any).research : undefined,
		});

		// WARNING: connectedList could be disconnected by changing route during ajax request
		this.connectedList?.setData(data);

		Bus.emit('SharedLayoutSetupService:toggle-left-aside', false);

		!targetPopable
			? useNavLoaderService().hide()
			: targetPopable._loader()?.hide();
	}

	async applyWithRouterAsync (resetPagination?: boolean) {
		if (router.currentRoute.value) {
			if (resetPagination) {
				this.filter.page.index = 1;
			}

			const filter = qsTo64(this.filter);
			router.push({
				...router.currentRoute.value,
				query: {
					...router.currentRoute.value.query,
					filter,
				},
			});
		}
	}

	showFilterAside () {
		if (!this.filterComponent) {
			throw `No filterComponent define in the pagedListFilterService`;
		}

		useAside({
			Nested: this.filterComponent,
			NestedProps: this.filterProps,
		});
	}
}
